import React, { useState, useEffect, useCallback } from 'react';
import { taskLogsAPI } from '../services/api';

const TaskLogs = ({ taskId, onLogAdded }) => {
  const [logs, setLogs] = useState([]);
  const [totalTime, setTotalTime] = useState(0);
  const [newLog, setNewLog] = useState('');
  const [statusChange, setStatusChange] = useState('');
  const [timeTaken, setTimeTaken] = useState('');
  const [loading, setLoading] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      const response = await taskLogsAPI.getLogs(taskId);
      console.log('Logs response:', response.data);
      console.log('Response type:', typeof response.data, 'Is array:', Array.isArray(response.data));
      
      // Handle both new format { logs: [], total_time: number } and old format (array)
      let logsArray, totalTimeValue;
      
      if (response.data && response.data.logs && Array.isArray(response.data.logs)) {
        // New format
        logsArray = response.data.logs;
        totalTimeValue = response.data.total_time || 0;
        console.log('Using new format - logs:', logsArray.length, 'total_time:', totalTimeValue);
      } else if (Array.isArray(response.data)) {
        // Old format (backward compatibility)
        logsArray = response.data;
        totalTimeValue = logsArray.reduce((sum, log) => sum + (parseInt(log.time_taken) || 0), 0);
        console.log('Using backward compatibility - logs:', logsArray.length, 'calculated total:', totalTimeValue);
      } else {
        logsArray = [];
        totalTimeValue = 0;
        console.log('Unexpected response format');
      }
      
      // Ensure time_taken is a number for each log
      const logsWithTime = logsArray.map(log => ({
        ...log,
        time_taken: parseInt(log.time_taken) || 0
      }));
      
      console.log('Final logs with time_taken:', logsWithTime.map(log => ({ id: log.id, time_taken: log.time_taken })));
      
      setLogs(logsWithTime);
      setTotalTime(totalTimeValue);
    } catch (err) {
      console.error('Error loading logs:', err);
    }
  }, [taskId]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newLog.trim()) return;

    setLoading(true);
    try {
      const timeValue = timeTaken && timeTaken.trim() !== '' ? parseInt(timeTaken) : 0;
      const logData = {
        log_entry: newLog,
        status_after: statusChange || undefined,
        time_taken: timeValue,
      };

      console.log('Submitting log with time_taken:', timeValue, 'from input:', timeTaken);
      await taskLogsAPI.createLog(taskId, logData);
      setNewLog('');
      setStatusChange('');
      setTimeTaken('');
      loadLogs();
      if (onLogAdded) onLogAdded();
    } catch (err) {
      alert('Failed to add log entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Task Logs</h3>
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div className="form-group">
          <label className="form-label">Add Log Entry</label>
          <textarea
            value={newLog}
            onChange={(e) => setNewLog(e.target.value)}
            placeholder="Describe progress, issues, or updates..."
            rows={3}
            className="form-textarea"
          />
        </div>

        <div style={styles.formRow}>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Update Status (Optional)</label>
            <select
              value={statusChange}
              onChange={(e) => setStatusChange(e.target.value)}
              className="form-select"
            >
              <option value="">No status change</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Time Taken (minutes)</label>
            <input
              type="number"
              value={timeTaken}
              onChange={(e) => setTimeTaken(e.target.value)}
              placeholder="0"
              min="0"
              className="form-input"
            />
            <small className="form-help">Enter time spent on this log entry</small>
          </div>
        </div>

        <button type="submit" disabled={loading} className="btn btn-primary">
          {loading ? 'Adding...' : 'Add Log Entry'}
        </button>
      </form>

      {totalTime > 0 && (
        <div style={styles.totalTime}>
          <strong>Total Time Spent: </strong>
          {formatTime(totalTime)}
        </div>
      )}

      <div style={styles.logsList}>
        <h4 style={styles.logsTitle}>Log History</h4>
        {logs.length === 0 ? (
          <p style={styles.empty}>No logs yet. Add your first log entry!</p>
        ) : (
          logs.map((log) => (
            <div key={log.id} style={styles.logItem}>
              <div style={styles.logHeader}>
                <span style={styles.logDate}>
                  {new Date(log.created_at).toLocaleString()}
                </span>
                {log.status_before && log.status_after && (
                  <span className="badge badge-info">
                    Status: {log.status_before} → {log.status_after}
                  </span>
                )}
              </div>
              <p style={styles.logText}>{log.log_entry}</p>
              <div style={styles.logTime}>
                <strong>Time Taken:</strong> {formatTime(parseInt(log.time_taken) || 0)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Helper function to format time in minutes to readable format
const formatTime = (minutes) => {
  if (!minutes) return '0 minutes';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ${mins} minute${mins > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  } else {
    return `${mins} minute${mins > 1 ? 's' : ''}`;
  }
};

const styles = {
  form: {
    marginBottom: '2rem',
  },
  logsList: {
    marginTop: '2rem',
    paddingTop: '2rem',
    borderTop: '1px solid #e5e7eb',
  },
  logsTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '1rem',
  },
  logItem: {
    backgroundColor: '#f8f9fa',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    border: '1px solid #e5e7eb',
  },
  logHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  logDate: {
    fontSize: '0.875rem',
    color: '#64748b',
    fontWeight: '500',
  },
  logText: {
    margin: 0,
    color: '#374151',
    lineHeight: '1.6',
  },
  empty: {
    color: '#64748b',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '2rem',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  },
  totalTime: {
    padding: '1rem',
    backgroundColor: '#e0f2fe',
    borderRadius: '8px',
    marginBottom: '1rem',
    border: '1px solid #bae6fd',
    color: '#0c4a6e',
  },
  logTime: {
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #e5e7eb',
    fontSize: '0.875rem',
    color: '#475569',
    fontWeight: '600',
  },
};

export default TaskLogs;
